import { useState, useMemo, useEffect } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Phone, Sparkles, Loader2, ImageIcon, AlertCircle } from 'lucide-react';
import { formatPhoneNumber } from '@/lib/utils';

export function PhoneGateStep() {
  const { state, setPhoneNumber, sendVerificationCode, isLoading, trackEvent, error: providerError, checkDuplicate, requestCallback, tenantConfig } = useWidget();

  const [phone, setPhone] = useState(state.contact.phone || '');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateCompany, setDuplicateCompany] = useState<string | null>(null);
  const [isRequestingCallback, setIsRequestingCallback] = useState(false);

  // Create preview thumbnails from local File objects or S3 URLs
  const previewUrls = useMemo(() => {
    if (state.imageFiles.length > 0) {
      return state.imageFiles.map((file) => URL.createObjectURL(file));
    }
    return state.uploadedImages;
  }, [state.imageFiles, state.uploadedImages]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    if (state.imageFiles.length > 0) {
      return () => {
        previewUrls.forEach((url) => {
          if (url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
      };
    }
  }, [previewUrls, state.imageFiles.length]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setError(null);
  };

  const validatePhone = (): boolean => {
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleSendCode = async () => {
    if (!validatePhone()) {
      return;
    }

    if (isSending || isLoading) {
      return; // Prevent double-click
    }

    setIsSending(true);
    setError(null);
    setIsDuplicate(false);
    setDuplicateCompany(null);

    try {
      const phoneDigits = phone.replace(/\D/g, '');

      // Check for duplicate phone number first
      const duplicateCheck = await checkDuplicate(phoneDigits);

      if (duplicateCheck.duplicate) {
        setIsDuplicate(true);
        setDuplicateCompany(duplicateCheck.companyName || tenantConfig?.company_name || 'this company');
        setIsSending(false);
        return;
      }

      // Store the phone number in state
      setPhoneNumber(phoneDigits);
      trackEvent('phone_entered');

      // Pass phone directly to avoid stale closure - no delay needed
      await sendVerificationCode(phoneDigits);

      trackEvent('code_sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsSending(false);
    }
  };

  const handleRequestCallback = async () => {
    setIsRequestingCallback(true);
    try {
      await requestCallback();
      setError('Callback requested! We will contact you shortly.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request callback');
    } finally {
      setIsRequestingCallback(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Duplicate Phone Number Warning */}
      {isDuplicate && (
        <Card className="p-4 bg-destructive/5 border-destructive/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="font-semibold text-sm">You already have a renovation preview</p>
              <p className="text-xs text-muted-foreground">
                You already have a renovation preview with {duplicateCompany}
              </p>
            </div>
          </div>
          <Button
            className="w-full"
            variant="default"
            onClick={handleRequestCallback}
            disabled={isRequestingCallback}
          >
            {isRequestingCallback ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting...
              </>
            ) : (
              'Request Callback'
            )}
          </Button>
        </Card>
      )}

      {/* Teaser Card with Original Photos */}
      {!isDuplicate && (
        <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="font-semibold text-sm">Your AI design is being prepared</p>
            <p className="text-xs text-muted-foreground">Verify your phone to see the final result</p>
          </div>
        </div>

        {/* Thumbnail strip of original photos */}
        {previewUrls.length > 0 && (
          <div className="flex gap-2 mt-2">
            {previewUrls.map((url, i) => (
              <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden shrink-0">
                <img
                  src={url}
                  alt={`Your photo ${i + 1}`}
                  className="w-full h-full object-cover brightness-75"
                />
              </div>
            ))}
          </div>
        )}
        {previewUrls.length === 0 && (
          <div className="flex gap-2 mt-2">
            <div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-muted-foreground" />
            </div>
          </div>
        )}
      </Card>
      )}

      {/* Phone Input */}
      {!isDuplicate && (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
                className={`text-lg ${error ? 'border-destructive' : ''}`}
                maxLength={14}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">
                We'll send a 6-digit code to verify your number
              </p>
            </div>
          </div>

          {/* Error Message from Provider */}
          {providerError && (
            <p className="text-sm text-destructive text-center">{providerError}</p>
          )}

          {/* Send Code Button */}
          <Button
            className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
            size="lg"
            onClick={handleSendCode}
            disabled={isSending || isLoading}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending code...
              </>
            ) : (
              'Send Verification Code'
            )}
          </Button>

          {/* Trust signals */}
          <p className="text-xs text-center text-muted-foreground">
            Your phone number is only used to verify your identity and contact you about your project.
          </p>
        </>
      )}
    </div>
  );
}
